import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, catchError, switchMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import * as IcpActions from './icp.actions';
import { IcpService } from '../../services/icp.service';
import { PopupService } from '../../services/popup.service';

@Injectable()
export class IcpEffects {
  constructor(
    private actions$: Actions,
    private icpService: IcpService,
    private popupService: PopupService,
    private router: Router
  ) {}

  loadIcps$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IcpActions.loadIcps),
      switchMap((action) =>
        this.icpService.getIcps({
          page: action.page,
          per_page: action.perPage,
          search: action.search,
          filter: action.filter,
          include: action.include
        }).pipe(
          map((response) =>
            IcpActions.loadIcpsSuccess({
              icps: response.data,
              pagination: response.pagination
            })
          ),
          catchError((error) => {
            // Suppress 401 errors (user logged out) - don't dispatch failure
            if (error.status === 401) {
              console.log('ICP load failed: User not authenticated');
              return of(IcpActions.loadIcpsSuccess({ icps: [], pagination: null }));
            }
            return of(IcpActions.loadIcpsFailure({ error }));
          })
        )
      )
    )
  );

  getIcpDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IcpActions.getIcpDetail),
      switchMap((action) =>
        this.icpService.getIcpDetail(action.id).pipe(
          map((response) =>
            IcpActions.getIcpDetailSuccess({ icp: response.data })
          ),
          catchError((error) =>
            of(IcpActions.getIcpDetailFailure({ error }))
          )
        )
      )
    )
  );

  loadIcpDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IcpActions.loadIcpDetail),
      switchMap((action) =>
        this.icpService.getIcpDetail(action.id).pipe(
          map((response) =>
            IcpActions.loadIcpDetailSuccess({ icp: response.data })
          ),
          catchError((error) =>
            of(IcpActions.loadIcpDetailFailure({ error }))
          )
        )
      )
    )
  );

  createIcp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IcpActions.createIcp),
      switchMap((action) =>
        this.icpService.createIcp(action.icp).pipe(
          map((response) =>
            IcpActions.createIcpSuccess({ icp: response.data })
          ),
          catchError((error) => {
            // Suppress 401 errors (user logged out)
            if (error.status === 401) {
              console.log('ICP create failed: User not authenticated');
              return of(IcpActions.createIcpSuccess({ icp: null }));
            }
            return of(IcpActions.createIcpFailure({ error }));
          })
        )
      )
    )
  );

  createIcpSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(IcpActions.createIcpSuccess),
        tap((action) => {
          // Don't show success notification if ICP is null (suppressed 401 error)
          if (action.icp) {
            this.popupService.notify(
              'ICP created successfully',
              'Success',
              'success'
            );
            this.router.navigate(['/icp']);
          }
        })
      ),
    { dispatch: false }
  );

  updateIcp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IcpActions.updateIcp),
      switchMap((action) =>
        this.icpService.updateIcp(action.id, action.icp).pipe(
          map((response) =>
            IcpActions.updateIcpSuccess({ icp: response.data })
          ),
          catchError((error) =>
            of(IcpActions.updateIcpFailure({ error }))
          )
        )
      )
    )
  );

  updateIcpSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(IcpActions.updateIcpSuccess),
        tap((action) => {
          this.popupService.notify(
            'ICP updated successfully',
            'Success',
            'success'
          );
          this.router.navigate(['/icp']);
        })
      ),
    { dispatch: false }
  );

  deleteIcp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IcpActions.deleteIcp),
      switchMap((action) =>
        this.icpService.deleteIcp(action.id).pipe(
          map(() =>
            IcpActions.deleteIcpSuccess({ id: action.id })
          ),
          catchError((error) =>
            of(IcpActions.deleteIcpFailure({ error }))
          )
        )
      )
    )
  );

  deleteIcpSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(IcpActions.deleteIcpSuccess),
        tap(() => {
          this.popupService.notify(
            'ICP deleted successfully',
            'Success',
            'success'
          );
        })
      ),
    { dispatch: false }
  );

  // Clone ICP
  cloneIcp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IcpActions.cloneIcp),
      switchMap((action) =>
        this.icpService.cloneIcp(action.id, action.name).pipe(
          map((response) =>
            IcpActions.cloneIcpSuccess({ icp: response.data })
          ),
          catchError((error) =>
            of(IcpActions.cloneIcpFailure({ error }))
          )
        )
      )
    )
  );

  cloneIcpSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(IcpActions.cloneIcpSuccess),
        tap((action) => {
          this.popupService.notify(
            'ICP duplicated successfully',
            'Success',
            'success'
          );
        })
      ),
    { dispatch: false }
  );

  cloneIcpFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(IcpActions.cloneIcpFailure),
        tap((action) => {
          const message = action.error?.error?.message || action.error?.message || 'Failed to duplicate ICP';
          this.popupService.notify(message, 'Error', 'error');
        })
      ),
    { dispatch: false }
  );

  // Error handling effects
  createIcpFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(IcpActions.createIcpFailure),
        tap((action) => {
          this.popupService.notify(
            action.error.message || 'Failed to create ICP',
            'Error',
            'error'
          );
        })
      ),
    { dispatch: false }
  );

  updateIcpFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(IcpActions.updateIcpFailure),
        tap((action) => {
          this.popupService.notify(
            action.error.message || 'Failed to update ICP',
            'Error',
            'error'
          );
        })
      ),
    { dispatch: false }
  );

  deleteIcpFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(IcpActions.deleteIcpFailure),
        tap((action) => {
          let errorMessage = 'Failed to delete ICP';
          
          // Handle specific API error scenarios
          if (action.error.status === 404) {
            errorMessage = 'ICP not found or you don\'t have permission to delete it';
          } else if (action.error.status === 409) {
            errorMessage = 'Cannot delete ICP. It may be linked to workspaces or is the default ICP.';
          } else if (action.error.message) {
            errorMessage = action.error.message;
          }

          this.popupService.notify(
            errorMessage,
            'Error',
            'error'
          );
        })
      ),
    { dispatch: false }
  );
}